import { GCSEOHead, CTABanner, Breadcrumbs, RelatedGuides, LastUpdated } from "./GCLayout";
import { gcCanonicalBase } from "./gcDomain";
import { JsonLd } from "../../components/JsonLd";
import { ArrowRight, Check, AlertCircle } from "lucide-react";

const SECTIONS = [
  {
    title: "Identity & Civil Documents",
    description: "Prove who you are and your personal history.",
    items: [
      { doc: "Valid Passport", note: "Must be valid for at least 6 months beyond planned entry", required: true },
      { doc: "Birth Certificate", note: "Original or certified copy with English translation", required: true },
      { doc: "Marriage Certificate", note: "If applicable. Include divorce/death certificates from prior marriages", required: true },
      { doc: "Divorce Decree(s)", note: "For all prior marriages, if applicable", required: false },
      { doc: "National ID Card", note: "If issued by your country", required: false },
      { doc: "Name Change Documents", note: "Court orders or legal name change certificates", required: false },
    ],
  },
  {
    title: "Photos & Biometrics",
    description: "USCIS-compliant photos and fingerprints.",
    items: [
      { doc: "2 Passport-Style Photos", note: "2x2 inches, white background, taken within 30 days", required: true },
      { doc: "Biometrics Appointment (ASC)", note: "USCIS will schedule after filing. Bring appointment notice + ID", required: true },
    ],
  },
  {
    title: "Immigration Forms",
    description: "The specific forms depend on your Green Card category.",
    items: [
      { doc: "Form I-485", note: "Application to Register Permanent Residence (if adjusting in the U.S.)", required: true },
      { doc: "Form I-130", note: "Petition for Alien Relative (family-based)", required: true },
      { doc: "Form I-140", note: "Immigrant Petition for Alien Workers (employment-based)", required: true },
      { doc: "Form I-864", note: "Affidavit of Support (financial sponsor form)", required: true },
      { doc: "Form I-693", note: "Medical Examination Report (sealed envelope from USCIS-approved doctor)", required: true },
      { doc: "Form I-765", note: "Application for Employment Authorization (work permit while waiting)", required: false },
      { doc: "Form I-131", note: "Application for Travel Document (Advance Parole)", required: false },
    ],
  },
  {
    title: "Financial Documents",
    description: "Prove financial ability to support yourself or your sponsor's ability to support you.",
    items: [
      { doc: "Tax Returns (3 years)", note: "IRS transcripts or signed returns with W-2s for sponsor", required: true },
      { doc: "Employment Letter", note: "Current employer letter with salary, position, start date", required: true },
      { doc: "Pay Stubs (Recent)", note: "Last 3-6 months of pay stubs for sponsor", required: true },
      { doc: "Bank Statements", note: "Last 3-6 months showing sufficient savings", required: true },
      { doc: "Asset Documentation", note: "Property deeds, investment accounts (if needed to meet income threshold)", required: false },
    ],
  },
  {
    title: "Employment Documents (Employment-Based Only)",
    description: "For EB-1, EB-2, EB-3, and EB-5 categories.",
    items: [
      { doc: "PERM Labor Certification", note: "Approved ETA Form 9089 (EB-2/EB-3)", required: true },
      { doc: "Job Offer Letter", note: "Detailed letter from sponsoring employer", required: true },
      { doc: "Resume / CV", note: "Comprehensive work history", required: true },
      { doc: "Degree Certificates + Transcripts", note: "With credential evaluation if foreign degrees", required: true },
      { doc: "Professional Licenses", note: "If required for the position", required: false },
      { doc: "Letters of Recommendation", note: "From experts in your field (especially EB-1/EB-2 NIW)", required: false },
    ],
  },
  {
    title: "Police & Background Clearance",
    description: "Required for applicants 16 years and older.",
    items: [
      { doc: "Police Clearance Certificate", note: "From every country you lived in 6+ months since age 16", required: true },
      { doc: "Court Records", note: "If you have any arrests, charges, or convictions (even dismissed)", required: false },
      { doc: "Military Records", note: "If you served in any military", required: false },
    ],
  },
  {
    title: "Medical Examination",
    description: "Must be completed by a USCIS-designated civil surgeon.",
    items: [
      { doc: "Medical Exam (Form I-693)", note: "Includes physical exam, TB test, blood work", required: true },
      { doc: "Vaccination Records", note: "Proof of required immunizations (or get them at the exam)", required: true },
      { doc: "Sealed Envelope from Doctor", note: "Do NOT open. Submit sealed with your I-485", required: true },
    ],
  },
];

const DOCS_FAQS = [
  {
    q: "What happens if I submit the wrong documents?",
    a: "USCIS will issue a Request for Evidence (RFE) asking for the correct documents. This delays your case by weeks or months. In rare cases, incorrect documents can lead to a denial. Double-check every item against the official filing instructions before submitting.",
  },
  {
    q: "Do I need certified English translations?",
    a: "Yes. Any document not in English must include a certified English translation. The translator must certify that the translation is complete and accurate. USCIS does not accept partial translations or summaries.",
  },
  {
    q: "How recent do my documents need to be?",
    a: "Financial documents (bank statements, pay stubs) should be from the last 3-6 months. Tax returns must cover the most recent 3 years. Medical exams (I-693) are valid for 2 years. Passport photos must be taken within 30 days of filing.",
  },
  {
    q: "Can I submit copies instead of originals?",
    a: "For most documents, USCIS accepts clear, legible photocopies. However, some documents (like the medical exam in its sealed envelope) must be originals. Always keep originals in a safe place — USCIS may request them at any point during processing.",
  },
];

export default function GCDocumentsPage() {
  const base = gcCanonicalBase();
  const totalRequired = SECTIONS.reduce((acc, s) => acc + s.items.filter((i) => i.required).length, 0);
  const totalOptional = SECTIONS.reduce((acc, s) => acc + s.items.filter((i) => !i.required).length, 0);

  const allDocs = SECTIONS.flatMap((s) => s.items.filter((i) => i.required));

  return (
    <>
      <GCSEOHead
        title="Green Card Documents Checklist 2026: 32 Required Forms & Papers"
        description="Complete 2026 checklist of every document for a U.S. Green Card application - 22 required and 10 situational documents across 7 categories including identity, forms, financials, and medical."
        path="/documents-checklist"
      />

      {/* JSON-LD ItemList Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Green Card Required Documents Checklist",
            description: "Complete list of required documents for a U.S. Green Card application.",
            dateModified: "2026-02-01",
            url: `${base}/documents-checklist`,
            numberOfItems: allDocs.length,
            itemListElement: allDocs.map((d, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: d.doc,
              description: d.note,
            })),
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: DOCS_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Breadcrumbs */}
      <Breadcrumbs current="Documents Checklist" />

      {/* Header */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4" data-testid="gc-docs-h1">
            Green Card Documents Checklist
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
            Missing a single document is one of the top reasons Green Card applications get delayed.
            This checklist covers everything you need — organized by category so you can prepare
            your packet with confidence before you file.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="bg-emerald-100 text-emerald-800 text-sm font-medium px-4 py-2 rounded-lg">
              {totalRequired} required documents
            </div>
            <div className="bg-slate-100 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg">
              {totalOptional} situational documents
            </div>
          </div>
          <div className="mt-4">
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-12 md:py-16" data-testid="gc-docs-checklist">
        <div className="max-w-4xl mx-auto px-4 space-y-10">
          {SECTIONS.map((section, i) => (
            <div key={section.title}>
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading">
                  {i + 1}. {section.title}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{section.description}</p>
              </div>
              <div className="space-y-2">
                {section.items.map((item) => (
                  <div
                    key={item.doc}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      item.required
                        ? "border-emerald-100 bg-emerald-50/50"
                        : "border-slate-100 bg-slate-50/50"
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {item.required ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-slate-800">{item.doc}</span>
                        {item.required ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">Required</span>
                        ) : (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">If applicable</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{item.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="py-10 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">Pro Tips for Document Preparation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Make copies of everything before submitting originals",
              "Get certified English translations for all non-English documents",
              "Keep documents organized by category in a binder or folder",
              "Medical exam (I-693) is valid for only 2 years from the date of the civil surgeon's signature",
              "Tax transcripts can be ordered free from the IRS website",
              "Police clearance certificates may take 2-8 weeks to obtain from some countries",
            ].map((tip, i) => (
              <div key={i} className="flex gap-2 text-sm text-slate-600">
                <span className="text-emerald-600 font-bold flex-shrink-0">{i + 1}.</span>
                {tip}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16" data-testid="gc-docs-faq">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading mb-6">
            Documents FAQ
          </h2>
          <div className="space-y-6">
            {DOCS_FAQS.map((faq) => (
              <div key={faq.q}>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4 text-base">
            Want help organizing your documents and tracking what is ready?
          </p>
          <a
            href="https://immigrationguide.us/auth?mode=register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="gc-docs-cta"
          >
            Track your documents for free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* DV Lottery Resources */}
      <section className="py-10 border-t border-slate-100" data-testid="gc-docs-dv-resources">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">DV Lottery Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="https://immigrationguide.us/how-to-check-dv-lottery-results" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">How to Check DV Lottery Results</h3>
              <p className="text-xs text-slate-500">Official steps and deadlines for the 2026 cycle</p>
            </a>
            <a href="https://immigrationguide.us/dv-lottery-eligibility" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">DV Lottery Eligibility</h3>
              <p className="text-xs text-slate-500">Country, education, and work experience requirements</p>
            </a>
          </div>
        </div>
      </section>

      <RelatedGuides currentPath="/documents-checklist" />
      <CTABanner />
    </>
  );
}
